import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import "../../../assets/styles/rdcstatus.css";
import { commonService } from "../../../services/commonService";
import Pagination from '../../../components/Common/Pagination';
import Loader from '../../../components/Common/Loader';

const RDCDownloadsStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const [downloads, setDownloads] = useState<any[]>([]);
  const [pageDetail, setPageDetail] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Loader for row download
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Popup error message
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const payload = {
        page: 1,
        page_size: pageSize,
      };

      try {
        const response = await commonService.rdcDownloadList(payload);
        setDownloads(response?.data);

        const page_data = {
          page: response.page,
          total_pages: response.total_pages,
          total_records: response.total,
          page_size: response.page_size,
        };
        setPageDetail(page_data);
      } catch (err) {
        setErrorMsg("Failed to load records.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchData = async (newPage: any) => {
    const payload = {
      page: newPage,
      page_size: pageSize,
    };

    try {
      const response = await commonService.rdcDownloadList(payload);
      setDownloads(response?.data);

      const page_data = {
        page: response.page,
        total_pages: response.total_pages,
        total_records: response.total,
        page_size: response.page_size,
      };
      setPageDetail(page_data);
    } catch (err) {
      setErrorMsg("Failed to load records.");
    } finally {
      setLoading(false);
    }
  };

  const formatHeader = (header: string) => {
    return header
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const downloadCSV = (rows: any[], filename = "download.csv") => {
    if (!rows || rows.length === 0) {
      throw new Error("No data available.");
    }

    const headers = Object.keys(rows[0]);
    const displayHeaders = headers.map(formatHeader);

    const csvContent =
      displayHeaders.join(",") +
      "\n" +
      rows
        .map((row) =>
          headers
            .map((header) => {
              const cell = row[header] ?? "";
              return `"${String(cell).replace(/"/g, '""')}"`;
            })
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  const downloadData = async (request_id: string) => {
    if (!request_id) return;

    try {
      setDownloadingId(request_id);

      const response = await commonService.rdcDownload(request_id);

      if (!response?.data?.records) {
        throw new Error("No download data found.");
      }

      downloadCSV(response.data.records, request_id + ".csv");
    } catch (error: any) {
      setErrorMsg(error.message || "Failed to download data.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <MainLayout>
      <div className="rde-downloads-page" style={{ backgroundColor: "#f0f8ff" }}>

        {/* Header */}
        <div className="rde-header">
          <div className="rde-back-button-container">
            <button onClick={() => navigate(-1)} className="rde-back-button">
              <BackIcon />
              Back
            </button>
          </div>
          <h4 className="rde-title">RDC Downloads Status</h4>
        </div>

        {/* Main Table */}
        <div>
          {loading ? (
            <Loader
              text='Loading...' />
          ) : (
            <div className="mt-3 rt-tablewrap">
              <table className="table table-bordered table-striped text-center">
                <thead className="table-primary">
                  <tr>
                    <th>Sl No</th>
                    <th>RDC Match Upload Id</th>
                    <th>Retrieving Count</th>
                    {/* <th>Max Contact per Company</th> */}
                    <th>Maximum Contacts Found</th>
                    <th>Status</th>
                    <th>Created At</th>
                  </tr>
                </thead>

                <tbody>
                  {downloads.length > 0 ? (
                    downloads.map((item, index) => (
                      <tr key={item.request_id || index}>
                        <td data-label="Sl No">{((pageDetail.page - 1) * pageDetail.page_size) + (index + 1)}</td>
                        <td data-label="RDC Match Upload Id">{item.request_id}</td>
                        <td data-label="Retrieving Count">{item.no_of_contacts}</td>
                        {/* <td>{item.max_contact_per_company}</td> */}
                        <td data-label="Maximum Contacts Found">{item.downloaded_count}</td>
                        <td data-label="Status">
                          <button
                            disabled={
                              downloadingId !== null ||
                              item.status !== "completed"
                            }
                            onClick={() => downloadData(item.request_id)}
                            style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              backgroundColor: item.status == 'completed' ? "#218d3aff" : "rgba(186, 213, 66, 1)",
                              color: "#ffffff",
                            }}
                          >
                            {downloadingId === item.request_id
                              ? "Downloading..."
                              : item.status === "completed"
                                ? "Ready to download"
                                : item.status === "processing"
                                  ? "In Progress"
                                  : item.status.charAt(0).toUpperCase() +
                                  item.status.slice(1).toLowerCase()}
                          </button>
                        </td>

                        <td data-label="Created At">
                          {new Date(item.created_at).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7}>No download records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          page={pageDetail.page}
          total_pages={pageDetail.total_pages}
          total_records={pageDetail.total_records}
          page_size={pageDetail.page_size}
          onPageChange={(newPage) => fetchData(newPage)}
        />

        {/* Error Popup */}
        {errorMsg && (
          <div className="popup-overlay">
            <div className="popup">
              <p>{errorMsg}</p>
              <button onClick={() => setErrorMsg(null)} style={{
                backgroundColor: "#61A1D4",
                padding: "3px 10px",
                borderRadius: "7px",
                textAlign: "center",
                color: "white",
                border: "none",
                cursor: "pointer"
              }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RDCDownloadsStatusPage;
